import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, Phone, Star, Zap, ChevronRight } from '../../lib/icons';
import SEOHead from '../../components/SEOHead';

const PRODUCTS = [
  {
    id: 'tempered-glass',
    name: 'Tempered Glass',
    subtitle: 'Standard Protection',
    price: 'From $20',
    badge: null,
    description: 'High-quality tempered glass applied professionally while your device is in for service. Shields against scratches, everyday drops, and impacts.',
    features: [
      'Hardness-rated glass layer',
      'Crystal-clear optical clarity',
      'Applied by appointment',
      '30-day warranty',
    ],
    devices: ['iPhone (all models)', 'iPad (all models)', 'Samsung Galaxy'],
    color: 'from-[#0EA5E9] to-[#2563EB]',
    accent: '#0EA5E9',
  },
  {
    id: 'privacy-glass',
    name: 'Privacy Tempered Glass',
    subtitle: 'Privacy + Protection',
    price: 'From $25',
    badge: 'Popular',
    description: 'Blocks side-angle viewing so only the person directly in front of the screen can see it. Combines security with impact protection.',
    features: [
      'Blocks 45° side-view angle',
      'Tempered glass durability',
      'Professional installation',
      '30-day warranty',
    ],
    devices: ['iPhone (all models)', 'iPad (all models)', 'Samsung Galaxy'],
    color: 'from-[#8B5CF6] to-[#6D28D9]',
    accent: '#8B5CF6',
  },
  {
    id: 'prot-pro-ultra2',
    name: 'ProtectionPro Ultra 2',
    subtitle: 'by Madico',
    price: 'From $45',
    badge: 'Premium',
    description: 'Self-healing TPU film from Madico\'s ProtectionPro product line. Virtually invisible, absorbs impacts, and minor surface scratches self-heal with heat.',
    features: [
      'Self-healing TPU film technology',
      'Virtually invisible application',
      'Absorbs impact energy',
      'Minor scratch self-repair',
      '90-day warranty',
      'Available for many device types',
    ],
    devices: ['iPhone', 'iPad', 'Samsung Galaxy', 'Samsung Tablets', 'Apple Watch', 'Many more'],
    color: 'from-[#0EA5E9] to-[#10B981]',
    accent: '#0EA5E9',
    featured: true,
  },
  {
    id: 'prot-pro-matte',
    name: 'ProtectionPro Matte',
    subtitle: 'by Madico',
    price: 'From $45',
    badge: null,
    description: 'Anti-glare self-healing film from Madico. Eliminates fingerprints, reduces screen glare in bright environments, and self-heals minor scratches.',
    features: [
      'Anti-glare matte finish',
      'Fingerprint resistance',
      'Self-healing TPU technology',
      'Reduces eye strain outdoors',
      '90-day warranty',
      'Available for many device types',
    ],
    devices: ['iPhone', 'iPad', 'Samsung Galaxy', 'Samsung Tablets', 'Apple Watch', 'Many more'],
    color: 'from-[#64748B] to-[#475569]',
    accent: '#94A3B8',
  },
];

const WHEN_TO_PROTECT = [
  { title: 'After a screen replacement', body: 'The best time to add protection. Your screen is already factory-fresh — seal it before the first drop.' },
  { title: 'When you get a new device', body: 'New devices deserve new protection. Start with a pristine surface for the best possible application.' },
  { title: 'When your current protector cracks', body: 'A cracked protector has done its job. Time to replace it before your screen takes the next hit.' },
];

const DEVICE_COMPAT = [
  { label: 'iPhone', models: 'All current and recent models including iPhone 16 series' },
  { label: 'iPad', models: 'All iPad Pro, Air, mini, and standard models' },
  { label: 'Samsung Galaxy', models: 'S-series and A-series — confirm your model when booking' },
  { label: 'Samsung Tablets', models: 'Galaxy Tab S series — confirm when booking' },
  { label: 'Apple Watch', models: 'Select ProtectionPro sizes — ask us about your model' },
  { label: 'Other devices', models: 'ProtectionPro supports many tablets and devices — contact us' },
];

const ScreenProtectionPage = () => (
  <div className="bg-[#050B14] min-h-screen font-sans">
    <SEOHead
      title="Screen Protection — Tempered Glass & ProtectionPro by Madico | GameFix Solutions"
      description="Professional screen protection installation in Temecula, CA. Tempered glass, privacy glass, and ProtectionPro Ultra 2 & Matte films by Madico for iPhone, iPad, Samsung, and more."
      keywords="screen protector temecula, tempered glass iphone temecula, protection pro madico, iphone screen protection, privacy glass installation, samsung screen protector"
      canonical="https://gamefixsolutions.com/phone-repair/screen-protection"
    />

    {/* ── Hero ──────────────────────────────────────────────────────────────── */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] to-[#050B14]" />
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: 'radial-gradient(ellipse at 80% 20%, rgba(14,165,233,0.2) 0%, transparent 60%)' }}
      />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link to="/phone-repair" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Phone Repair</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-700" />
          <span className="text-slate-400">Screen Protection</span>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#10B981] flex items-center justify-center mb-5">
          <Shield className="h-7 w-7 text-white" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
          Screen Protection<br />
          <span className="bg-gradient-to-r from-[#0EA5E9] to-[#10B981] bg-clip-text text-transparent">
            Done Right
          </span>
        </h1>
        <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-2xl">
          Professional-grade tempered glass and ProtectionPro films by Madico — applied
          correctly the first time. Especially powerful when added right after a screen replacement.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/book-repair"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold transition-colors">
            Book a Repair + Protection <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="tel:+19512168534"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-[#0EA5E9]/60 text-slate-300 hover:text-white font-semibold transition-colors">
            <Phone className="h-4 w-4" /> (951) 216-8534
          </a>
        </div>
      </div>
    </section>

    {/* ── Products ──────────────────────────────────────────────────────────── */}
    <section className="py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-2">Protection Options</h2>
          <p className="text-slate-400 max-w-lg mx-auto">From everyday tempered glass to premium self-healing film — choose the level of protection that fits your lifestyle.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {PRODUCTS.map(product => (
            <div
              key={product.id}
              className={`relative bg-[#0A1628] border rounded-2xl p-7 ${product.featured ? 'border-[#0EA5E9]/40' : 'border-slate-700/50'}`}
            >
              {product.badge && (
                <span className={`absolute top-4 right-4 px-2.5 py-1 rounded-full text-xs font-bold ${
                  product.badge === 'Premium'
                    ? 'bg-[#0EA5E9]/20 text-[#0EA5E9] border border-[#0EA5E9]/30'
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                }`}>
                  {product.badge}
                </span>
              )}

              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${product.color} flex items-center justify-center mb-4`}>
                <Shield className="h-5 w-5 text-white" />
              </div>

              <div className="mb-1">
                <h3 className="text-white font-bold text-xl">{product.name}</h3>
                {product.subtitle && <p className="text-slate-500 text-sm">{product.subtitle}</p>}
              </div>
              <p className="font-bold text-2xl mb-3" style={{ color: product.accent }}>{product.price}</p>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">{product.description}</p>

              <ul className="space-y-1.5 mb-5">
                {product.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: product.accent }} />
                    {f}
                  </li>
                ))}
              </ul>

              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Compatible Devices</p>
                <div className="flex flex-wrap gap-1.5">
                  {product.devices.map(d => (
                    <span key={d} className="px-2 py-0.5 rounded bg-slate-800 text-slate-400 text-xs">{d}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── When to protect ───────────────────────────────────────────────────── */}
    <section className="py-14 bg-[#0A1628]/50 border-y border-slate-800/60">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">The Right Time to Add Protection</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {WHEN_TO_PROTECT.map((w, i) => (
            <div key={i} className="bg-[#050B14] border border-slate-800/60 rounded-xl p-6">
              <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex items-center justify-center mb-3">
                <Star className="h-4 w-4 text-[#0EA5E9]" />
              </div>
              <h3 className="text-white font-semibold mb-2 text-sm">{w.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Device compatibility ──────────────────────────────────────────────── */}
    <section className="py-14 lg:py-18">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Device Compatibility</h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              ProtectionPro by Madico supports a wide range of devices beyond just phones.
              Contact us to confirm compatibility for your specific model.
            </p>
            <div className="space-y-3">
              {DEVICE_COMPAT.map(d => (
                <div key={d.label} className="flex gap-3 bg-[#0A1628] border border-slate-700/50 rounded-xl p-4">
                  <CheckCircle className="h-4 w-4 text-[#10B981] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-sm">{d.label}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{d.models}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-[#0A1F3A] to-[#0A1628] border border-[#0EA5E9]/25 rounded-2xl p-6">
              <Zap className="h-6 w-6 text-[#0EA5E9] mb-3" />
              <h3 className="text-white font-bold text-lg mb-2">Bundle With Any Repair</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                Add screen protection to any repair appointment and we'll install it while your device is already with us.
                The perfect time — especially after a screen replacement.
              </p>
              <Link to="/book-repair"
                className="inline-flex items-center gap-2 text-[#0EA5E9] font-semibold text-sm hover:text-white transition-colors">
                Book a Repair + Protection <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-white font-bold mb-2">Protection-Only Appointment</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                Not getting a repair? You can book a standalone screen protection appointment.
                We'll apply your choice of tempered glass or ProtectionPro film at a scheduled time.
              </p>
              <a href="tel:+19512168534"
                className="inline-flex items-center gap-2 text-slate-400 font-semibold text-sm hover:text-white transition-colors">
                <Phone className="h-4 w-4" /> Call to schedule — (951) 216-8534
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA ───────────────────────────────────────────────────────────────── */}
    <section className="py-12 border-t border-slate-800/60">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Protect your device before you leave</h2>
        <p className="text-slate-400 mb-6">Add it to your repair — or book a standalone protection appointment.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/book-repair"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold transition-colors">
            Book a Repair <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/phone-repair"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold transition-colors">
            All Phone Services
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default ScreenProtectionPage;
